import {useState, useEffect, useLayoutEffect} from 'react';
import {useParams, useLocation} from 'react-router';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import PageShell from '../components/PageShell';
import * as DentalKnowledgeApi from '../api/DentalKnowledgeApi';
import './DentalKnowledgementArticle.css';

/**
 * DentalKnowledgementArticle renders a single dental education article.
 *
 * It fetches the article metadata (title, date) from the backend API,
 * then fetches the markdown content from CloudFront and renders it
 * using ReactMarkdown with GFM table support.
 *
 * Route: /dental-education/:knowledgeId
 *
 * @return {React.ReactElement} The rendered article page component.
 */
export default function DentalKnowledgementArticle() {
  const {knowledgeId} = useParams();
  const {hash} = useLocation();
  const [dentalKnowledge, setDentalKnowledge] = useState({});
  const [dentalKnowledgeEssay, setDentalKnowledgeEssay] = useState('');
  const [videos, setVideos] = useState([]);

  // Fetch article metadata (title, update_date, content_markdown URL)
  useEffect(() => {
    DentalKnowledgeApi.getDentalKnowledge(knowledgeId)
      .then((res) => setDentalKnowledge(res))
      .catch((err) => {
        console.error('Failed to fetch dental education article:', err);
      });
  }, [knowledgeId]);

  // Fetch the markdown content from the CloudFront URL
  // once we have the content_markdown URL from the metadata
  useEffect(() => {
    if (dentalKnowledge.content_markdown) {
      DentalKnowledgeApi.getDentalKnowledgeMarkdown(dentalKnowledge.content_markdown)
        .then((res) => setDentalKnowledgeEssay(res))
        .catch((err) => {
          console.error('Failed to fetch markdown content:', err);
          setDentalKnowledgeEssay('');
        });
    }

    if (dentalKnowledge.title) {
      DentalKnowledgeApi.getDentalVideos(dentalKnowledge.title)
        .then((res) => setVideos(res))
        .catch((err) => {
          console.error('Failed to fetch videos:', err);
        });
    }
  }, [dentalKnowledge]);
  // Scroll to hash if present after videos load
  useLayoutEffect(() => {
    if (videos.length > 0 && hash === '#videos') {
      setTimeout(() => {
        const el = document.getElementById('videos');
        if (el) el.scrollIntoView({behavior: 'smooth'});
      }, 100);
    }
  }, [videos, hash]);

  return (
    <PageShell title={dentalKnowledge.title || 'Loading...'} showBackButton={true}>
      <article className="dental-article-container">
        {/* Hero image */}
        {dentalKnowledge.cover_image && (
          <div className="dental-article-hero">
            <img src={dentalKnowledge.cover_image} alt="" className="dental-article-hero-img" />
          </div>
        )}

        <div className="article-markdown-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{dentalKnowledgeEssay}</ReactMarkdown>
        </div>

        {/* Videos section */}
        {videos.length > 0 && (
          <section style={{marginTop: 32}} id="videos">
            <h2
              style={{
                margin: '0 0 16px',
                fontFamily: 'var(--font-display), Outfit, system-ui',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: '#c43b71',
                lineHeight: 1.3,
              }}
            >
              Watch
            </h2>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
              }}
            >
              {videos.map((video) => {
                // If the URL is an iframe (like embed_video plugin often outputs) we can just use dangerouslySetInnerHTML
                // But typically DentalVideo URL is just a youtube link. Let's see if the backend returns the raw url or iframe.
                // Assuming it's a youtube URL, we can extract the ID.
                let youtubeId = '';
                try {
                  const urlObj = new URL(video.url);
                  if (urlObj.hostname.includes('youtube.com')) {
                    youtubeId = urlObj.searchParams.get('v');
                  } else if (urlObj.hostname.includes('youtu.be')) {
                    youtubeId = urlObj.pathname.slice(1);
                  }
                } catch {
                  // Not a parseable URL: render as a plain link below.
                }

                if (youtubeId) {
                  return (
                    <div
                      key={video.id}
                      style={{
                        position: 'relative',
                        width: '100%',
                        paddingBottom: '56.25%', // 16:9 aspect ratio
                        borderRadius: 16,
                        overflow: 'hidden',
                        background: 'rgba(26, 26, 46, 0.06)',
                      }}
                    >
                      <iframe
                        src={`https://www.youtube-nocookie.com/embed/${youtubeId}`}
                        title={video.title}
                        loading="lazy"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none',
                        }}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        referrerPolicy="strict-origin-when-cross-origin"
                      />
                    </div>
                  );
                }

                return (
                  <div key={video.id}>
                    <a href={video.url} target="_blank" rel="noreferrer" style={{color: '#c43b71'}}>
                      {video.title}
                    </a>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </article>
    </PageShell>
  );
}
